import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/hooks/use-language';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import type { HomeCollectionsBlock } from '@/lib/homeContent';

interface CollectionCardProps {
  title: string;
  subtitle: string;
  description: string;
  image: string;
  link: string;
  isActive?: boolean;
  onToggle?: () => void;
  isMobile?: boolean;
}

const CollectionCard: React.FC<CollectionCardProps> = ({ 
  title, 
  subtitle, 
  description, 
  image, 
  link,
  isActive,
  onToggle,
  isMobile
}) => {
  const { t } = useLanguage();
  
  const CardContent = (
    <>
      <div className="aspect-[3/4.5] sm:aspect-[4/5] overflow-hidden relative">
        <img
          src={image}
          alt={title}
          loading="lazy"
          className={cn(
            "w-full h-full object-cover transition-all duration-300 ease-in-out",
            isMobile 
              ? (isActive ? "grayscale-0 opacity-100 scale-100" : "grayscale opacity-50 scale-100") 
              : "grayscale group-hover:grayscale-0 group-hover:scale-110"
          )}
          draggable="false"
        />
        <div className={cn(
          "absolute inset-0 bg-gradient-to-t from-background/90 via-transparent to-transparent transition-opacity duration-300 pointer-events-none",
          isMobile
            ? (isActive ? "opacity-0" : "opacity-70")
            : "opacity-60 group-hover:opacity-20"
        )}></div>
      </div>
      <div className="flex flex-col flex-1" style={{ padding: 'clamp(0.625rem, 1.5vw, 2rem)', gap: 'clamp(0.4rem, 1vw, 1rem)' }}>
        <h3 
          className={cn(
            "font-bold uppercase tracking-tighter transition-colors duration-300",
            isMobile 
              ? (isActive ? "text-primary" : "text-white") 
              : "text-primary"
          )} 
          style={{ fontSize: 'clamp(0.75rem, 1.5vw, 1.5rem)' }}
        >
          {title}
        </h3>
        <div className="uppercase tracking-[0.2em] text-white/40 font-bold" style={{ fontSize: 'clamp(0.55rem, 0.75vw, 0.75rem)' }}>
          {subtitle}
        </div>
        <p className="text-white/50 leading-relaxed flex-1 hidden sm:block" style={{ fontSize: 'clamp(0.75rem, 0.875vw, 0.875rem)' }}>
          {description}
        </p>
        <Link 
          to={link} 
          onClick={(e) => isMobile && e.stopPropagation()}
          className="pt-1 sm:pt-2 flex items-center gap-2 text-primary uppercase tracking-widest font-bold transition-all duration-300 hover:opacity-70 active:scale-95 origin-left" 
          style={{ fontSize: 'clamp(0.55rem, 0.75vw, 0.75rem)' }}
        >
          {t('home.collections.view_all')}
        </Link>
      </div>
    </>
  );

  if (isMobile) {
    return (
      <div 
        onClick={onToggle}
        className={cn(
          "group flex flex-col h-full mirror-card transition-all duration-300 collection-card !bg-[#13100B]",
          isActive ? "is-active border-primary/50 shadow-[0_0_20px_rgba(176,138,90,0.15)]" : "border-primary/10"
        )}
      >
        {CardContent}
      </div>
    );
  }

  return (
    <Link to={link} className="group flex flex-col h-full mirror-card hover:border-primary/30 transition-all duration-300 !bg-[#13100B]">
      {CardContent}
    </Link>
  );
};

export const Collections: React.FC<{ dbData?: HomeCollectionsBlock }> = ({ dbData }) => {
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  // Static fallback collections (used when DB has no data or empty cards)
  const staticCollections = [
    {
      title: t('home.collections.laminate.title'),
      subtitle: t('home.collections.laminate.subtitle'),
      description: t('home.collections.laminate.desc'),
      image: "https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FdZzPoYdCAnZsoOJ53C8y1ea1Nu83%2F5aa9e7bfcc4dde52f3770ff6ac82846d_3ec56745-7b11-4969-a79b-20fe04123699__2422de4c.png?alt=media&token=5b135a80-4208-4ec5-a54b-c8d22f7f77ef",
      link: "/catalog?series=SPC LAMINATE"
    },
    {
      title: t('home.collections.panels.title'),
      subtitle: t('home.collections.panels.subtitle'),
      description: t('home.collections.panels.desc'),
      image: "https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FdZzPoYdCAnZsoOJ53C8y1ea1Nu83%2Fe2b20807a8b551a31a64513879d0f431_f20af20a-aa5e-4ad5-a27e-0806dc3be3161__3717002b.png?alt=media&token=b13b01f9-b7bd-4a58-94c7-890ca543ff58",
      link: "/catalog?series=SPC PANELS"
    },
    {
      title: t('home.collections.carpet.title'),
      subtitle: t('home.collections.carpet.subtitle'),
      description: t('home.collections.carpet.desc'),
      image: "https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FdZzPoYdCAnZsoOJ53C8y1ea1Nu83%2F481ff3dbf1669ab2fd28e25775c9640f_8e68dc9a-ae17-4c8c-a920-cab5011d53fb__72675039.png?alt=media&token=03638ccd-18dc-48e5-a618-e6cf216e7bfc",
      link: "/catalog?series=CARPET TILE"
    },
    {
      title: t('home.collections.accessories.title'),
      subtitle: t('home.collections.accessories.subtitle'),
      description: t('home.collections.accessories.desc'),
      image: "https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FdZzPoYdCAnZsoOJ53C8y1ea1Nu83%2F8eb50455f55dd8ad8d4e60fe7845f0d4_f89fddef-2e1d-47a4-91c5-1e8e8f16bb19__26021fa0.png?alt=media&token=4f07510e-ac66-4622-9c29-3596ae212eb0",
      link: "/catalog?series=ACCESSORIES"
    }
  ];

  // dbData === undefined means homeData hasn't loaded yet — suppress static fallback during load
  if (dbData === undefined) return null;

  // Use DB cards if available, otherwise use static fallback (homeData loaded but no cards in DB)
  const hasDbCards = dbData.cards && dbData.cards.length > 0;
  const collections = hasDbCards
    ? dbData.cards.map(card => ({
        title: card.name,
        subtitle: (card as any).subtitle ?? '',
        description: card.description,
        image: card.image,
        link: card.url,
      }))
    : staticCollections;

  // Title/subtitle: use DB if non-empty, fallback to t()
  const sectionTitle = (dbData?.title?.trim()) || t('home.collections.title');
  const sectionSubtitle = (dbData?.subtitle?.trim()) || t('home.collections.subtitle');

  return (
    <section className="py-16 lg:py-24 relative overflow-hidden bg-[#13100B]">
      <div className="grid-cells opacity-20" />
      <div className="container-custom relative z-10">
        <div className="mb-12 lg:mb-16">
          <h2
            className="font-extrabold uppercase tracking-tighter text-3xl lg:text-5xl text-white"
            dangerouslySetInnerHTML={{ __html: sectionTitle }}
          />
          <p className="text-white/40 uppercase tracking-[0.2em] text-[10px] lg:text-xs mt-4">
            {sectionSubtitle}
          </p>
        </div>

        <div 
          className="grid grid-cols-2 lg:grid-cols-4 gap-[12px] md:gap-[var(--gap-collections)]"
        >
          {collections.map((item, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: idx * 0.1 }}
              className="w-full"
            >
              <CollectionCard 
                {...item} 
                isMobile={isMobile}
                isActive={activeIndex === idx}
                onToggle={() => setActiveIndex(activeIndex === idx ? null : idx)}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};